import type { SurfaceKey } from "types/interfaces/access";
import { ACTIONS, can, canInClientView, openAction } from "utils/access";
import type { RoleProfile } from "utils/roles";
import { integrationsHomePath, isClientPortalUser } from "utils/roles";

const surface = (s: SurfaceKey) => openAction(s);

function join(basePath: string, path: string): string {
  if (!path || path === "/") return basePath || "/";
  return `${basePath}${path}`;
}

const stripTrailingSlash = (value: string) =>
  value.length > 1 ? value.replace(/\/+$/, "") : value;

/**
 * Active-state test for a nav entry: the exact path or a descendant of it,
 * never a sibling that merely shares a prefix ("/integrations" must not light
 * up on "/integrations-beta"). Callers special-case their home entry, which
 * is a prefix of everything.
 */
export function isNavPathActive(pathname: string, path: string): boolean {
  const current = stripTrailingSlash(pathname);
  const target = stripTrailingSlash(path);
  if (!target || target === "/") return current === "" || current === "/";
  return current === target || current.startsWith(`${target}/`);
}

export type NavSectionKey =
  | "dataIntegrations"
  | "workspace"
  | "partners"
  | "management";

/** Keys follow the pages' surfaces; only the labels say what people read. */
export type NavProductKey =
  | "home"
  | "dataStorage"
  | "integrations"
  | "integrationsPlatform"
  | "botsManagement"
  | "sftpManagement"
  | "secureExchange"
  | "ai"
  | "partnerDirectory"
  | "partnerManagement"
  | "userManagement"
  | "accessGroups";

/** A home tile: the full one, or the small one (icon and title only). */
export type NavTile =
  | { kind: "product"; description: string; cta: string }
  | { kind: "small" };

export interface NavProduct {
  key: NavProductKey;
  label: string;
  /** Inside the Client View shell when viewing as someone. */
  path: string;
  /** Home has none. */
  tile: NavTile | null;
  /** Active on its own path only; every other product also on the pages below it. */
  exact: boolean;
}

export interface NavSection {
  key: NavSectionKey;
  /** Title Case; the sidebar and the home page upper-case it with CSS. */
  title: string;
  products: NavProduct[];
}

export interface NavCatalog {
  home: NavProduct;
  /** Only the sections with something to show, in order. */
  sections: NavSection[];
}

/** Viewing as someone: their catalog, never more than the viewer holds. */
export interface NavClientView {
  basePath: string;
  viewer: RoleProfile;
}

interface ProductEntry {
  key: Exclude<NavProductKey, "home">;
  label: string;
  /** A function when the destination depends on the user. */
  path: string | ((profile: RoleProfile) => string);
  action: string;
  /** More actions the destination needs for this user. */
  alsoNeeds?: (profile: RoleProfile) => string[];
  tile: NavTile;
  /** Never offered in Client View. */
  outsideClientView?: true;
}

const SMALL_TILE: NavTile = { kind: "small" };

/**
 * Every page the sidebar and the home page offer, in the sections both show:
 * one entry per product - its label, where it leads, the action that opens it
 * and its home tile.
 */
const CATALOG: {
  key: NavSectionKey;
  title: string;
  products: ProductEntry[];
}[] = [
  {
    key: "dataIntegrations",
    title: "Data & Integrations",
    products: [
      {
        key: "dataStorage",
        label: "Data Storage",
        path: "/data-storage",
        action: surface("data_storage"),
        tile: {
          kind: "product",
          description:
            "Access files from data migrations stored securely on your account.",
          cta: "Open Storage",
        },
      },
      {
        key: "integrations",
        label: "Integrations",
        // A client portal user's Integrations is the Integration Platform.
        path: integrationsHomePath,
        action: surface("integrations"),
        // There the platform's own gate asks for the client portal as well,
        // of the viewer too in Client View.
        alsoNeeds: (profile) =>
          isClientPortalUser(profile)
            ? [ACTIONS.INTEGRATIONS_PLATFORM_PORTAL]
            : [],
        tile: {
          kind: "product",
          description:
            "Monitor live integrations, their history, and their schedules.",
          cta: "View Integrations",
        },
      },
      {
        key: "integrationsPlatform",
        label: "Integrations Platform",
        path: "/integrations-beta",
        action: surface("integrations_platform"),
        tile: {
          kind: "product",
          description:
            "Build, configure and run integrations on the new platform.",
          cta: "Open Platform",
        },
      },
      {
        key: "botsManagement",
        label: "Automations",
        path: "/bots-management",
        action: surface("bots_management"),
        tile: {
          kind: "product",
          description:
            "Run extraction & upload tools, split files, and review data.",
          cta: "Open Automated Tools",
        },
      },
      {
        key: "sftpManagement",
        label: "SFTP Setup",
        path: "/sftp-management",
        action: surface("sftp_management"),
        tile: {
          kind: "product",
          description:
            "Configure the connections for client data repositories.",
          cta: "Set Up SFTPs",
        },
      },
    ],
  },
  {
    key: "workspace",
    title: "Workspace",
    products: [
      {
        key: "secureExchange",
        label: "Secure Exchange",
        path: "/secure-exchange",
        action: surface("secure_exchange"),
        tile: {
          kind: "product",
          description: "Share files securely with your CalibrateHCM team.",
          cta: "Exchange Files",
        },
      },
      {
        key: "ai",
        label: "AI Reporting",
        path: "/chat",
        action: ACTIONS.AI_AGENT_OPEN,
        tile: {
          kind: "product",
          description: "Explore your workforce data across every system.",
          cta: "Launch AI Agent",
        },
      },
    ],
  },
  {
    key: "partners",
    title: "Partners",
    products: [
      {
        key: "partnerDirectory",
        label: "Partner Directory",
        path: "/partners",
        action: surface("partner_directory"),
        tile: {
          kind: "product",
          description:
            "Browse CalibrateHCM's service and integration partners.",
          cta: "Explore All Partners",
        },
      },
      {
        key: "partnerManagement",
        label: "Partner Management",
        path: "/partner-management",
        action: surface("partner_management"),
        tile: {
          kind: "product",
          description:
            "Set up or edit the partners that display in the directory.",
          cta: "Configure Partners",
        },
      },
    ],
  },
  {
    key: "management",
    title: "Management",
    products: [
      {
        key: "userManagement",
        label: "Client & User Setup",
        path: "/user-management",
        action: surface("user_management"),
        tile: SMALL_TILE,
      },
      {
        key: "accessGroups",
        label: "Access Groups",
        path: "/access-groups",
        action: ACTIONS.ACCESS_GROUPS_MANAGE,
        tile: SMALL_TILE,
        // Group setup is never previewed as someone else; the page has no
        // Client View route.
        outsideClientView: true,
      },
    ],
  },
];

/**
 * What one person is offered. Pass `clientView` when viewing as `profile`: the
 * catalog is then theirs, inside the shell, and never more than the viewer holds.
 */
export function getNavCatalog(
  profile: RoleProfile,
  clientView?: NavClientView,
): NavCatalog {
  const basePath = clientView?.basePath ?? "";
  const allowed = (action: string) =>
    clientView
      ? canInClientView(profile, clientView.viewer, action)
      : can(profile, action);
  const offered = (entry: ProductEntry) =>
    !(clientView && entry.outsideClientView) &&
    [entry.action, ...(entry.alsoNeeds?.(profile) ?? [])].every(allowed);
  return {
    home: {
      key: "home",
      label: "Home",
      path: join(basePath, "/"),
      tile: null,
      exact: true,
    },
    sections: CATALOG.map((section) => ({
      key: section.key,
      title: section.title,
      products: section.products.filter(offered).map((entry) => ({
        key: entry.key,
        label: entry.label,
        path: join(
          basePath,
          typeof entry.path === "function" ? entry.path(profile) : entry.path,
        ),
        tile: entry.tile,
        exact: false,
      })),
    })).filter((section) => section.products.length > 0),
  };
}

/** Whether the catalog offers this product (Home always). */
export function catalogOffers(
  catalog: NavCatalog,
  key: NavProductKey,
): boolean {
  return (
    key === "home" ||
    catalog.sections.some((section) =>
      section.products.some((product) => product.key === key),
    )
  );
}

export function isProductActive(
  product: NavProduct,
  pathname: string,
): boolean {
  if (!product.exact) return isNavPathActive(pathname, product.path);
  return (
    (stripTrailingSlash(pathname) || "/") === stripTrailingSlash(product.path)
  );
}

/** The one product lit for a page: the longest path that covers it, or none. */
export function activeNavKey(
  catalog: NavCatalog,
  pathname: string,
): NavProductKey | null {
  let lit: NavProduct | null = null;
  for (const product of [
    catalog.home,
    ...catalog.sections.flatMap((section) => section.products),
  ]) {
    if (
      isProductActive(product, pathname) &&
      (lit === null || product.path.length > lit.path.length)
    ) {
      lit = product;
    }
  }
  return lit?.key ?? null;
}
